import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import ReadoutRail from './rail';
import ReadoutSectionContent from './section-content';
import {
  SECTIONS,
  PROGRAM_TYPES,
  programTypeMeta,
  sectionById,
  getFieldValue,
} from '../../../utils/tas/program-builder/sections';
import type { SectionId } from '../../../utils/tas/program-builder/sections';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];
type ChipIntent = 'success' | 'warning' | 'default';

interface ProgramTemplateReadoutSignature {
  Args: {
    model: TASProgramTemplateModel;
  };
  Element: HTMLDivElement;
}

// Builder sections the employer-facing readout never shows: Review & Launch is
// edit-only, and Recommendations & Audit is internal AI configuration.
const EXCLUDED_SECTIONS: ReadonlySet<string> = new Set(['review', 'recommendations']);

/**
 * Strict read-only view of a program template in the reimagined builder's
 * visual language: a passive section rail over static read-out panels (the
 * shared ReadoutSectionContent). Built for the employer-admin experience — no
 * inputs, no field-name badges, no save/suspend/launch/edit affordances
 * anywhere. Sections that don't apply to the program type are hidden entirely
 * (no N/A tabs), and Review & Launch and Recommendations & Audit are omitted.
 */
export default class ProgramTemplateReadout extends Component<ProgramTemplateReadoutSignature> {
  @service declare intl: IntlService;

  @tracked activeSectionId: SectionId = 'basics';

  goTo = (id: SectionId) => {
    this.activeSectionId = id;
  };

  get fields(): FieldsSignature {
    return this.args.model.fields;
  }

  get programType(): string {
    return String(getFieldValue(this.fields, 'TYPE') ?? '');
  }

  get typeMeta() {
    return programTypeMeta(this.programType);
  }

  /** Empty when the type is unset/unknown so the rail hides the type chip. */
  get typeShort(): string {
    return PROGRAM_TYPES[this.programType]?.short ?? '';
  }

  get programName(): string {
    return String(getFieldValue(this.fields, 'PROGRAM_NAME') ?? '');
  }

  get stateChipIntent(): ChipIntent {
    if (this.args.model.isLaunched) return 'success';
    if (this.args.model.isSuspended) return 'warning';
    return 'default';
  }

  get stateChipLabel(): string {
    if (this.args.model.isLaunched) {
      return this.intl.t('program_builder.readout.state_launched');
    }
    if (this.args.model.isSuspended) {
      return this.intl.t('program_builder.readout.state_suspended');
    }
    return this.intl.t('program_builder.readout.state_draft');
  }

  /** Applicable sections only — N/A and excluded sections are hidden entirely. */
  get visibleSections() {
    return SECTIONS.filter(
      (section) => !EXCLUDED_SECTIONS.has(section.id) && section.applies(this.typeMeta) !== 'na'
    );
  }

  get railItems() {
    return this.visibleSections.map((section) => ({
      id: section.id,
      label: this.intl.t(
        this.typeMeta.trackingOnly && section.trackingOnlyLabelKey
          ? section.trackingOnlyLabelKey
          : section.titleKey
      ),
      icon: section.icon,
      isActive: section.id === this.activeSectionId,
    }));
  }

  get activeSectionTitle(): string {
    const section = sectionById(this.activeSectionId);
    return this.intl.t(
      this.typeMeta.trackingOnly && section.trackingOnlyTitleKey
        ? section.trackingOnlyTitleKey
        : section.titleKey
    );
  }

  get activeSectionDesc(): string {
    const section = sectionById(this.activeSectionId);
    return this.intl.t(
      this.typeMeta.trackingOnly && section.trackingOnlyDescKey
        ? section.trackingOnlyDescKey
        : section.descKey
    );
  }

  /**
   * Two-column shell (rail · content). Inline style rather than a Tailwind
   * arbitrary class: this build's scanner doesn't emit `grid-cols-[…]`, which
   * would silently collapse the layout to one column.
   */
  get gridStyle() {
    return htmlSafe('grid-template-columns: 260px minmax(0, 1fr)');
  }

  <template>
    <div data-test-program-readout class="tio-fg-primary pb-8" ...attributes>
      <div class="grid items-start gap-8" style={{this.gridStyle}}>
        <ReadoutRail
          @items={{this.railItems}}
          @programName={{this.programName}}
          @typeShort={{this.typeShort}}
          @stateLabel={{this.stateChipLabel}}
          @stateIntent={{this.stateChipIntent}}
          @onSelect={{this.goTo}}
          class="sticky top-4"
        />

        <main class="min-w-0">
          <div class="mb-6">
            <h2 class="tio-text-2xl-semibold" data-test-readout-section-title>
              {{this.activeSectionTitle}}
            </h2>
            <p class="mb-0 mt-1 tio-fg-muted">{{this.activeSectionDesc}}</p>
          </div>

          <ReadoutSectionContent
            @model={{@model}}
            @fields={{this.fields}}
            @typeMeta={{this.typeMeta}}
            @activeSectionId={{this.activeSectionId}}
          />
        </main>
      </div>
    </div>
  </template>
}
